import { cfg } from './config.js';
import weaponsData from './content/weapons.json';
import enemiesData from './content/enemies.json';
import type { EnemyDef, PortraitStyle, Settings, Vec2, WeaponDef } from './types.js';
import { WorldState } from './sim/state.js';
import { FixedStepDriver, type Stepper } from './sim/fixed-step.js';
import type { GameEvent } from './sim/events.js';
import { updatePlayer, resetPlayerMovementContext } from './sim/movement.js';
import { updateWeapons, updateProjectiles, fireManualLaser } from './sim/weapon-system.js';
import { updateEnemies } from './sim/enemy-ai.js';
import { updateDirector, getWaveInfo, callWaveEarly } from './sim/director.js';
import { updateSerenity, getSerenityMods } from './sim/serenity.js';
import { getTraitMods, rollWeaponSlots } from './sim/loadout.js';
import { rollShipName } from './sim/ship-names.js';
import { updateWormhole } from './sim/wormhole.js';
import { BonusGame } from './sim/bonus-game.js';
import { SUBSPACE_KILL_SCORE } from './sim/subspace.js';
import { LoadoutScreen } from './ui/loadout-screen.js';
import { applyLoadout, type Loadout } from './sim/loadout.js';
import { loadLoadout } from './ui/storage.js';
import { recordWaveClear, recordBossKill } from './sim/decorations.js';
import { captainFromSeed, rollCaptainSeed, type CaptainRecord } from './captain/seed.js';
import { compositeCaptain } from './captain/compositor.js';
import { CaptainSpeech } from './captain/speech.js';
import { CaptainMoods } from './captain/moods.js';
import { pickLine, pickDeathLine, type CaptainMood } from './captain/dictionary.js';
import { saveCaptainDNA } from './captain/dna.js';
import { captainEffects } from './captain/effects.js';
import { rollCaptainQuirks, rollShipQuirks, applyQuirkMods } from './sim/quirks.js';
import {
  freshCaptainSkills,
  noteKill,
  noteDistance,
  noteDamageAbsorbed,
  noteTurretHit,
  notePickup,
  noteBravery,
  noteFlawless,
  SKILL_PIP_MODS,
  type CaptainSkills,
  type SkillId,
} from './captain/skills.js';
import { renderShipPreview } from './render/ship-preview.js';
import { EventsManager, type GameEventDef } from './sim/events-manager.js';
import { SpaceStormEvent } from './sim/space-storm.js';
import { wrapArena, wrappedDelta } from './sim/collision.js';
import { GameRenderer } from './render/renderer.js';
import type { GalaxyZone } from './render/background-objects.js';
import { AudioEngine } from './audio/audio-engine.js';
import { Hud } from './ui/hud.js';
import { Menus, type MenuCallbacks, type PauseInfo } from './ui/menus.js';
import {
  UpgradeSystem,
  getUpgradeMods,
  resolveLootCrate,
  applyUpgradeToPlayer,
  LOOT_CRATE_MOTES,
} from './sim/upgrade-system.js';
import { UpgradeOverlay } from './ui/upgrade-overlay.js';
import {
  applyShopItem,
  boonRarityFor,
  canAfford,
  describeStock,
  priceFor,
  rollShopBoon,
  rollShopStock,
  shopItem,
  FLASH_CELL_CHARGES,
  HULL_AMOUNT,
  REPAIR_AMOUNT,
  VITALS_AMOUNT,
  type ShopItemId,
} from './sim/shop.js';
import { ShopOverlay, type ShopEntryView } from './ui/shop-overlay.js';
import { loadSettings, saveSettings, detectTouchDevice, hasStoredSettings } from './ui/storage.js';
import { Onboarding } from './ui/onboarding.js';
import type { RunScore } from './ui/theme.js';
import { InputManager } from './input/input-state.js';
import { Haptics, HAPTIC_PATTERNS } from './input/haptics.js';

const STEP = 1 / 60;

// --- Creative timed events (Agent B) ---------------------------------------
// Event definitions come from the custom-events registry (sim/events/):
// self-contained deterministic defs with rarity/danger/minWave metadata,
// consumed here once — adding an event never touches this file again.
import { CUSTOM_EVENT_DEFS } from './sim/events/index.js';

// Score weights (per-run, deterministic: derived from sim events and tick).
const SCORE_PER_KILL = 100;
const SCORE_PER_PICKUP = 25;
const SCORE_PER_SECOND = 10;
/** Denial bonus per gestating (still-telegraphing) enemy killed. */
const GESTATION_BONUS = 25;
const ARENA_SIZE = cfg.getNumber('sim.arena_size');
const HALF_ARENA = ARENA_SIZE / 2;
/** Base magnet pull radius (arena units): motes home only inside this ring.
 * GDD §4.2 base pickup radius 3.0u. Playtest 2026-08-04 walked 12 → 3 → 2
 * and landed back on the spec value: 2u made manual trawling a chore, 3u
 * keeps early game hands-on without starving mote income. Mote Lure
 * (+25%/rank), Serenity tier 1 (×2) and captain traits scale it up. */
const MAGNET_PULL_RADIUS = 3;
// XP motes use a two-scale toroidal mass field: the local 3×3 cells keep
// blobs soft and stable, then every occupied cell contributes a faint capped
// far-field pull so isolated clouds slowly coagulate across the whole map.
const PICKUP_CLUSTER_CELL_SIZE = 8;
const PICKUP_CLUSTER_CELLS = Math.ceil(ARENA_SIZE / PICKUP_CLUSTER_CELL_SIZE);
const PICKUP_CLUSTER_CELL_COUNT = PICKUP_CLUSTER_CELLS * PICKUP_CLUSTER_CELLS;
const PICKUP_CLUSTER_RADIUS = 10;
const PICKUP_CLUSTER_PULL = 5.5;
const PICKUP_CLUSTER_REPEL = 10;
const PICKUP_CLUSTER_LONG_PULL = 16;
const PICKUP_CLUSTER_LONG_FALLOFF = 0.035;
const PICKUP_CLUSTER_LONG_FORCE_CAP = 1.1;
const PICKUP_CLUSTER_DRIFT = 1.35;
const PICKUP_DRAG_PER_STEP = 0.94;
const PICKUP_MAX_SPEED = 14;

// HP regen (deterministic, sim-tick based; never wall-clock): 1 HP/s once
// the player has gone 3 s without taking damage. Same damage gate the shield
// regen in combat.ts uses (last_damage_tick).
const HP_REGEN_DELAY_TICKS = 180; // 3 s at 60 Hz
const HP_REGEN_PER_TICK = 1 / 60;
// Green galaxy zones heal 5 HP/s while the player floats inside one.
const GALAXY_HEAL_PER_TICK = 5 / 60;

function wrapAxisDelta(a: number, b: number): number {
  const d = a - b;
  if (d > HALF_ARENA) return d - ARENA_SIZE;
  if (d < -HALF_ARENA) return d + ARENA_SIZE;
  return d;
}

function pickupClusterCellCoord(v: number): number {
  const raw = Math.floor((v + HALF_ARENA) / PICKUP_CLUSTER_CELL_SIZE);
  const wrapped = raw % PICKUP_CLUSTER_CELLS;
  return wrapped < 0 ? wrapped + PICKUP_CLUSTER_CELLS : wrapped;
}

function findWeapon(id: string): WeaponDef {
  const list = (weaponsData as { entries: WeaponDef[] }).entries;
  const def = list.find((w) => w.id === id);
  if (!def) throw new Error(`${id} not found`);
  return def;
}

function findEnemy(): EnemyDef {
  const list = (enemiesData as { entries: EnemyDef[] }).entries;
  const def = list.find((e) => e.id === 'enemy.driftfin');
  if (!def) throw new Error('enemy.driftfin not found');
  return def;
}

export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly world: WorldState;
  private readonly renderer: GameRenderer;
  private readonly audio: AudioEngine;
  private readonly hud: Hud;
  private readonly menus: Menus;
  private readonly driver: FixedStepDriver;
  private readonly inputManager: InputManager;
  private loadout: Loadout;
  private readonly loadoutScreen: LoadoutScreen;
  /** Live boss entity ids, so boss kills can be told apart from trash kills. */
  private readonly bossIds = new Set<number>();
  // Ship captain: hired per run, dies for real. The pending seed is the
  // captain the next run will hire (shown on the loadout screen).
  private captainSeed = 1;
  private pendingCaptainSeed = rollCaptainSeed();
  private captain: CaptainRecord = captainFromSeed(1);
  private captainPortrait: HTMLCanvasElement | null = null;
  private captainPortraitSeed = -1;
  private captainPortraitStyle: PortraitStyle | null = null;
  private readonly captainSpeech: CaptainSpeech;
  private readonly captainMoods: CaptainMoods;
  // First-run hint chip (ui/onboarding.ts): once per profile, first ~60 s.
  private readonly onboarding: Onboarding;
  /** Touch-capable hardware: gates synthetic 'mouse' samples out of the
   * onboarding hint copy (see the input sampling in makeStepper). */
  private readonly touchDevice = detectTouchDevice();
  private lastSignificantTick = 0;
  private waveDamage = 0;
  /** The captain's final line, picked at game over for the memorial panel. */
  private lastDeathLine = '';
  private settings: Settings;
  private rngSeed = 1;
  /** Seed of the NEXT run; rolled eagerly so the loadout preview can show it. */
  private pendingSeed = 0;
  private paused = false;
  private audioStarted = false;
  private weaponDef: WeaponDef;
  private laserDef: WeaponDef;
  private enemyDef: EnemyDef;
  // Run score counters (fed by sim events; reset on restart).
  private scoreKills = 0;
  private scorePickups = 0;
  private scoreMotes = 0;
  /** Bravery bonus banked by calling waves early (persists for the run). */
  private scoreBravery = 0;
  /** Wave-clear bonuses banked on wave finalization (25 × wave). */
  private scoreWaves = 0;
  /** Denial bonuses banked for killing gestating (telegraphing) enemies. */
  private scoreGestation = 0;
  /** Points earned in the wormhole subspace (multiplier kills). */
  private scoreSubspace = 0;
  /** Score spent in the supply-drop shop (the wallet: total = earned − spent). */
  private scoreSpent = 0;
  /** The captain's FTL-style skill training: pips grow with use, die with
   * the captain (reset on restart alongside the new hire). */
  private captainSkills: CaptainSkills = freshCaptainSkills();
  /** Player position at the previous sim step (piloting XP distance). */
  private prevPlayerPos = { x: 0, y: 0 };
  /** DOM button path: consumed at the next sim step, same as Enter. */
  private pendingWaveCall = false;
  /** Carries the bonus into the WAVE banner fired by the same call. */
  private pendingBraveryBonus = 0;
  private readonly pickupClusterCounts = new Uint16Array(PICKUP_CLUSTER_CELL_COUNT);
  private readonly pickupClusterSumX = new Float32Array(PICKUP_CLUSTER_CELL_COUNT);
  private readonly pickupClusterSumY = new Float32Array(PICKUP_CLUSTER_CELL_COUNT);
  private readonly pickupClusterActiveCells = new Uint16Array(PICKUP_CLUSTER_CELL_COUNT);
  // Lumensurge level-up system + overlay (reset on restart).
  private readonly upgradeSystem: UpgradeSystem;
  private readonly upgradeOverlay: UpgradeOverlay;
  // Supply-drop shop: intermission store spending the score wallet. Stock
  // derives from runSeed + wave; ranks it grants stack onto the Lumensurge
  // ranks for max_rank gating (the BonusGame boonRanks pattern).
  private readonly shopOverlay: ShopOverlay;
  private shopRanks = new Map<string, number>();
  /** Wave the open shop's stock was rolled for (display wave). */
  private shopWave = 1;
  /** Wave the current SOLD set belongs to; -1 = no shop opened this run. */
  private shopSoldWave = -1;
  /** Items already bought during the current shop opening. */
  private readonly shopSold = new Set<ShopItemId>();
  /** DOM button path: consumed at the next sim step, same as the B key. */
  private pendingShopOpen = false;
  // Wormhole bonus game controller (reset on restart); state on world.bonus.
  private readonly bonusGame: BonusGame;
  // Timed run events: the EventsManager schedules them at seeded intervals
  // (space storm + the creative events in CREATIVE_EVENT_DEFS; reset on restart).
  private readonly eventsManager: EventsManager;
  private readonly spaceStorm: SpaceStormEvent;
  // Time-slow window before the boons modal appears (ticks remaining).
  private offerDelayTicks = 0;
  // Web Haptics feedback for ship events.
  private readonly haptics = new Haptics();
  // Player death sequence delay (ticks remaining before the game-over panel).
  private deathSequenceTicks = 0;
  /** Boss reveal slow-mo window (ticks remaining; 0 = full speed). */
  private bossIntroTicks = 0;
  /** Dynamic-resolution state (render_scale_auto): EMA frame time + cadence. */
  private lastAutoFrameAt = 0;
  private frameMsEma = 0;
  private nextScaleEvalAt = 0;
  private autoScale = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.settings = loadSettings();
    // Touch devices (navigator.maxTouchPoints > 0) default to the mobile
    // performance profile so mobile hardware can hold the 60 fps target.
    // loadSettings() already seeds this auto-detected default; re-assert it
    // here only on first boot, so an explicit stored preference — including
    // a deliberate opt-out on a touch device — is never overridden.
    if (detectTouchDevice() && !hasStoredSettings() && !this.settings.video.mobile_performance) {
      this.settings.video.mobile_performance = true;
    }
    this.weaponDef = findWeapon('weapon.spear-jelly-lance');
    this.laserDef = findWeapon('weapon.manual-laser-cannon');
    this.enemyDef = findEnemy();

    // Pool capacity is immutable once constructed, so the mobile enemy cap
    // (perf.mobile_max_enemies) is chosen up front and holds across every
    // restart(); the director already clamps spawning to pool capacity.
    this.world = this.settings.video.mobile_performance
      ? new WorldState(cfg.getNumber('perf.mobile_max_enemies'))
      : new WorldState();
    // Deterministic default seed; can be overridden for tests.
    this.rngSeed = 1;
    this.world.reset(this.rngSeed);
    this.pendingSeed = this.rollSeed();

    this.renderer = new GameRenderer(canvas, this.renderSettings(this.settings));
    this.autoScale = this.settings.video.render_scale;
    this.audio = new AudioEngine(this.settings);
    const uiContainer = document.getElementById('ui-layer') as HTMLElement;
    this.hud = new Hud(uiContainer);
    this.hud.setShowFps(cfg.getBoolean('dev.overlay'));
    // HUD CALL button: queues a bravery call consumed at the next sim step
    // (the same path as the Enter key, so both stay tick-aligned).
    this.hud.setCallWaveHandler(() => {
      this.pendingWaveCall = true;
    });
    // HUD SHOP button: queues an open consumed at the next sim step (the
    // same path as the B key, so both stay tick-aligned).
    this.hud.setShopHandler(() => {
      this.pendingShopOpen = true;
    });

    const callbacks: MenuCallbacks = {
      onResume: () => this.setPaused(false),
      // RESTART RUN = same seed (the pause-menu mockup): an identical retry —
      // same waves, same captain, same hull. ABANDON TO TITLE goes through
      // the loadout screen for a fresh captain + hull instead.
      onRestart: () => {
        this.restart(this.world.runSeed);
      },
      onLoadout: () => {
        this.inputManager.showTouchControls(false);
        this.hud.setVisible(false);
        this.loadoutScreen.show();
      },
      onSettingsChange: (s) => this.applySettings(s),
      onHapticsTest: () => this.haptics.play(HAPTIC_PATTERNS.levelUp),
      getHapticsEngineName: () => this.haptics.engineName
    };
    this.menus = new Menus(uiContainer, this.settings, callbacks);

    this.upgradeSystem = new UpgradeSystem(this.world);
    // Boon eligibility reads Lumensurge ranks so boons respect max_rank.
    this.bonusGame = new BonusGame(this.world, (id) => this.upgradeSystem.getRank(id));
    // Seeded event scheduling; reseeded from world.runSeed on every restart.
    this.eventsManager = new EventsManager(this.rngSeed);
    this.spaceStorm = new SpaceStormEvent(this.rngSeed);
    this.eventsManager.register(this.spaceStorm);
    // Creative timed events (Agent B): meteor shower, ion storm, gravity well.
    // Creative timed events from the custom-events registry (sim/events/):
    // meteor shower, ion storm, gravity well, and everything added later.
    for (const def of CUSTOM_EVENT_DEFS) this.eventsManager.register(def);
    this.upgradeOverlay = new UpgradeOverlay(uiContainer, {
      onPick: (i) => this.pickUpgrade(i),
      onReroll: () => this.rerollUpgrades(),
      // Pause the sim without showing the pause menu; the overlay is the UI.
      onSetPaused: (p) => { this.paused = p; }
    });
    this.shopOverlay = new ShopOverlay(uiContainer, {
      onBuy: (id) => this.buyShopItem(id),
      onLeave: () => this.closeShop(),
      // Same pause pattern as the upgrade overlay: no pause menu, just a halt.
      onSetPaused: (p) => { this.paused = p; }
    });

    this.loadout = loadLoadout();
    this.renderer.setShipBias({
      swift: this.loadout.swift,
      bulwark: this.loadout.bulwark,
      striker: this.loadout.striker,
    });
    this.loadoutScreen = new LoadoutScreen(uiContainer, {
      onConfirm: (l) => this.onLoadoutConfirm(l),
      getShipSeed: (reroll) => this.getUpcomingShipSeed(reroll),
      getCaptainSeed: (reroll) => this.getUpcomingCaptainSeed(reroll),
      getPortraitStyle: () => this.settings.video.portrait_style,
    });
    this.captainSpeech = new CaptainSpeech(uiContainer, (pitch) => this.audio.playChirp(pitch));
    // The bark lives inside the corner module (pilot seat), not over it.
    this.hud.hostCaptainSpeech(this.captainSpeech.element);
    this.captainMoods = new CaptainMoods((mood) => this.onCaptainMood(mood));
    // Completion persists the seen-flag so the hints never show twice.
    this.onboarding = new Onboarding(uiContainer, () => {
      this.settings.access.onboarding_seen = true;
      saveSettings(this.settings);
    });
    // Declutter the combat focal point: the theme parks the hint chip at
    // bottom: 32%, inside ~240 px of the ship on short screens — drop it to
    // the bottom edge so mid-combat the player stays unobstructed.
    const onboardEl = uiContainer.querySelector<HTMLElement>('.la-onboard');
    if (onboardEl) onboardEl.style.bottom = '9%';

    this.inputManager = new InputManager(canvas);

    this.driver = new FixedStepDriver(this.makeStepper());

    this.bindGlobalInput();
    this.renderer.bindEvents(this.world.events);
    this.audio.bindEvents(this.world.events);
    this.world.events.register((e) => {
      if (!this.settings.access.haptics_enabled) return;
      switch (e.type) {
        case 'player_hit':
          this.haptics.play(HAPTIC_PATTERNS.playerHit);
          break;
        case 'dash':
          if (e.payload.phase === 'start') this.haptics.play(HAPTIC_PATTERNS.dash);
          break;
        case 'death':
          // Boss takedown gets the celebration pattern, trash the light tick.
          this.haptics.play(e.payload.boss === true ? HAPTIC_PATTERNS.bossDefeated : HAPTIC_PATTERNS.kill);
          break;
        case 'pickup':
          this.haptics.play(HAPTIC_PATTERNS.pickup);
          break;
        case 'levelup_offer':
          this.haptics.play(HAPTIC_PATTERNS.levelupOffer);
          break;
        case 'levelup_pick':
          this.haptics.play(HAPTIC_PATTERNS.levelUp);
          break;
        case 'game_over':
          this.haptics.play(HAPTIC_PATTERNS.gameOver);
          break;
        case 'wave_start':
          this.haptics.play(HAPTIC_PATTERNS.waveStart);
          break;
        case 'wave_clear':
          this.haptics.play(HAPTIC_PATTERNS.waveClear);
          break;
        case 'boss_spawn':
          this.haptics.play(HAPTIC_PATTERNS.bossSpawn);
          break;
        case 'loot_crate':
          this.haptics.play(HAPTIC_PATTERNS.lootCrate);
          break;
        case 'nova':
          this.haptics.play(HAPTIC_PATTERNS.nova);
          break;
        case 'captain_skill':
          this.haptics.play(HAPTIC_PATTERNS.captainSkill);
          break;
        case 'phase_change':
          // Wormhole entry + storm onset are the phases worth a pulse.
          if (e.payload.system === 'wormhole' && e.payload.phase === 'entered') {
            this.haptics.play(HAPTIC_PATTERNS.wormhole);
          } else if (e.payload.system === 'space-storm' && e.payload.phase === 'start') {
            this.haptics.play(HAPTIC_PATTERNS.stormStart);
          }
          break;
        case 'hazard':
          // Meteor impacts only — starshower's bolt strikes are too frequent.
          if (e.payload.event === 'meteor_shower' && e.payload.phase === 'impact') {
            this.haptics.play(HAPTIC_PATTERNS.hazardImpact);
          }
          break;
      }
    });
    // Wave banners: assault start / boss warning / wave clear. A UI concern,
    // so separate from the haptics-gated handler above. Captain mood hooks
    // ride the same events (ungated — the captain reacts to everything).
    this.world.events.register((e) => {
      switch (e.type) {
        case 'wave_start': {
          // A bravery call this same step appends its bonus to the banner —
          // EXCEPT on boss waves: the boss warning stands alone (staging).
          const bravery = this.pendingBraveryBonus;
          this.pendingBraveryBonus = 0;
          const label = e.payload.boss ? `⚠ BOSS WAVE ${e.payload.wave} ⚠` : `WAVE ${e.payload.wave}`;
          this.hud.showBanner(
            !e.payload.boss && bravery > 0 ? `${label} · +${bravery}` : label,
            e.payload.boss ? 'boss' : 'wave'
          );
          this.captainMoods.noteWaveStart();
          this.waveDamage = 0;
          this.lastSignificantTick = e.tick;
          break;
        }
        case 'wave_clear': {
          // Wave finalization bonus: 25 × wave, banked into the score and
          // folded into the banner so the payoff is visible immediately.
          const wave = typeof e.payload.wave === 'number' ? e.payload.wave : 0;
          const clearBonus = 25 * wave;
          // Flawless (0 damage taken this wave): doubles the clear bonus.
          const flawless = this.waveDamage <= 0 && wave > 0;
          const total = clearBonus * (flawless ? 2 : 1);
          this.scoreWaves += total;
          if (flawless) {
            // Daring training: a spotless wave steels the captain's nerve.
            const lvl = noteFlawless(this.captainSkills);
            if (lvl !== null) this.applySkillLevel('daring', lvl, e.tick);
          }
          const label = flawless ? `FLAWLESS · +${total}` : total > 0 ? `WAVE CLEARED · +${total}` : 'WAVE CLEARED';
          this.hud.showBanner(label, 'clear');
          if (wave > 0) recordWaveClear(this.world, wave);
          this.captainMoods.noteWaveCleared(flawless);
          this.lastSignificantTick = e.tick;
          break;
        }
        case 'boss_spawn':
          if (typeof e.payload.enemy_id === 'number') this.bossIds.add(e.payload.enemy_id);
          this.captainMoods.noteBossSpawn();
          this.lastSignificantTick = e.tick;
          // Boss reveal beat: a short slow-motion window stages the entrance
          // (suppressed while the death cinematic owns dilation).
          if (this.deathSequenceTicks <= 0) {
            this.bossIntroTicks = 40;
            this.driver.dilation = 0.35;
          }
          break;
        case 'death':
          this.captainMoods.noteKill();
          this.lastSignificantTick = e.tick;
          if (typeof e.payload.enemy_id === 'number' && this.bossIds.delete(e.payload.enemy_id)) {
            recordBossKill(this.world, String(e.payload.def_id));
          }
          break;
        case 'player_hit': {
          const dmg = typeof e.payload.damage === 'number' ? e.payload.damage : 0;
          this.waveDamage += dmg;
          // Engineering training: damage sustained toughens the captain.
          if (dmg > 0) {
            const lvl = noteDamageAbsorbed(this.captainSkills, dmg);
            if (lvl !== null) this.applySkillLevel('engineering', lvl, e.tick);
          }
          this.captainMoods.noteHit();
          this.captainMoods.noteDamage(dmg);
          this.lastSignificantTick = e.tick;
          break;
        }
        case 'game_over':
          this.captainMoods.noteGameOver();
          break;
      }
    });
    this.world.events.register((e) => this.trackScoreEvent(e));
    this.world.events.register((e) => this.onboarding.onEvent(e));

    this.restart();
  }

  async start(): Promise<void> {
    // Gate the first run behind the ship loadout screen; SET SAIL begins it.
    // Touch controls hide while the screen is up so DASH never covers it.
    this.inputManager.showTouchControls(false);
    // Attract mode: the world runs as a living screensaver behind the
    // loadout screen — waves spawn, the ship defends itself, nothing can be
    // lost. onLoadoutConfirm → restart() resets everything for the real run.
    this.restart(1);
    this.driver.start();
    this.world.player.mercy_until = Number.MAX_SAFE_INTEGER;
    this.hud.setVisible(false);
    this.loadoutScreen.show();
  }

  /** First-run start after the loadout screen's SET SAIL confirm. */
  private async beginRun(): Promise<void> {
    this.restart();
    await this.audio.init();
    this.audioStarted = true;
    this.audio.startMusic(this.rngSeed);
    this.driver.start();
  }

  /** Loadout confirmed: persist the choice, reshape the ship, and either
   * start the first run or restart the current one with the new build. */
  private onLoadoutConfirm(loadout: Loadout): void {
    this.inputManager.showTouchControls(this.settings.access.show_touch_controls);
    this.hud.setVisible(true);
    this.loadout = loadout;
    this.renderer.setShipBias({
      swift: loadout.swift,
      bulwark: loadout.bulwark,
      striker: loadout.striker,
    });
    if (this.audioStarted) {
      this.restart();
    } else {
      void this.beginRun();
    }
  }

  dispose(): void {
    this.driver.stop();
    this.audio.stopMusic();
    this.renderer.dispose();
    this.hud.dispose();
    this.menus.hide();
    this.upgradeOverlay.dispose();
    this.shopOverlay.dispose();
    this.inputManager.dispose();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('blur', this.onBlur);
  }

  restart(seed?: number): void {
    if (seed !== undefined) {
      // Explicit-seed restart (RESTART RUN, tests, captures): an identical
      // retry — same waves AND the same captain, so captainSeed stays
      // untouched (it was rolled separately when this captain was hired).
      this.rngSeed = seed >>> 0;
    } else {
      // A fresh run gets a fresh seed: new waves, new ship. Explicit seeds
      // (tests, captures) stay deterministic.
      this.rngSeed = this.pendingSeed;
      this.pendingSeed = this.rollSeed();
      // The next run hires a new captain; the old one's fate is written.
      this.captainSeed = this.pendingCaptainSeed;
      this.pendingCaptainSeed = rollCaptainSeed();
    }
    // The enemy pool cap (perf.mobile_max_enemies under the mobile profile)
    // is fixed at construction; reset() below keeps the capped pool.
    resetPlayerMovementContext(this.world.player);
    this.world.reset(this.rngSeed);
    this.upgradeSystem.reset();
    this.bonusGame.reset();
    this.eventsManager.reset(this.world.runSeed);
    this.spaceStorm.reset(this.world.runSeed);
    this.upgradeOverlay.hide();
    this.shopOverlay.hide();
    this.shopRanks.clear();
    this.shopSold.clear();
    this.shopSoldWave = -1;
    this.pendingShopOpen = false;
    this.paused = false;
    this.menus.hide();
    this.scoreKills = 0;
    this.scorePickups = 0;
    this.scoreMotes = 0;
    this.scoreBravery = 0;
    this.scoreWaves = 0;
    this.scoreGestation = 0;
    this.scoreSubspace = 0;
    this.scoreSpent = 0;
    this.pendingWaveCall = false;
    this.pendingBraveryBonus = 0;

    const p = this.world.player;
    p.alive = true;
    p.pos = { x: 0, y: 0 };
    p.vel = { x: 0, y: 0 };
    p.hp = cfg.getNumber('player.max_hp');
    p.max_hp = cfg.getNumber('player.max_hp');
    p.hitbox_radius = 0.6;
    p.lumensurge_tier = 1;
    p.weapons = [{
      def_id: this.weaponDef.id,
      level: 1,
      evolved: false,
      cooldown_until: 0,
      last_target_id: 0,
      policy: this.weaponDef.targeting_policy,
      slot: 0
    }, {
      // Manual laser cannon: never auto-fires; fires on firePressed input
      // via fireManualLaser() with its own cooldown.
      def_id: this.laserDef.id,
      level: 1,
      evolved: false,
      cooldown_until: 0,
      last_target_id: 0,
      policy: this.laserDef.targeting_policy,
      slot: 1
    }];
    p.passives = [];
    p.active = null;
    p.dash = { active: false, dir: { x: 0, y: 0 }, iframes_until: 0, cooldown_until: 0, charges: 1, recharge_until: 0 };
    p.mercy_until = 0;
    p.focus_aim = { held: false, dir: { x: 0, y: 1 } };
    p.rerolls_used = 0;
    // Hull hardpoints for this run, rolled from the ship seed BEFORE
    // applyLoadout runs (order matters: applyLoadout re-derives the same
    // value from world.runSeed + the loadout's striker spread and applies
    // the striker ≥ 3 bonus — the two always agree).
    p.weapon_slots = rollWeaponSlots(this.rngSeed, this.loadout.striker);
    // Clear the duck-typed dash-haste fields the dash-cooldown upgrade and
    // galaxy-zone bonus attach (movement.ts reads `haste`).
    delete (p as unknown as { haste?: number }).haste;
    delete (p as unknown as { hasteFromUpgrades?: number }).hasteFromUpgrades;
    // Apply the ship loadout (starting weapon + trait stat deltas), then top
    // up HP so builds start full. Programmatic restarts (tests, captures)
    // bypass the loadout screen entirely.
    applyLoadout(this.world, this.loadout);
    // The captain's perk/drawback pair (captain/effects.ts): HP and regen
    // apply directly; the rest ride getTraitMods via player.captainEffects.
    // Captain + ship quirks add their minor mods on top (sim/quirks.ts).
    const cfx = captainEffects(this.captain);
    applyQuirkMods(cfx, [...rollCaptainQuirks(this.captainSeed), ...rollShipQuirks(this.rngSeed)]);
    p.max_hp += cfx.maxHpDelta;
    p.boon_shield_regen_mult *= 1 + cfx.shieldRegenMultBonus;
    p.captainEffects = cfx;
    p.hp = p.max_hp;
    this.loadoutScreen.hide();
    this.bossIds.clear();
    // New captain for the new run; their portrait composites lazily.
    this.captain = captainFromSeed(this.captainSeed);
    this.captainMoods.reset();
    this.captainSpeech.hide();
    this.onboarding.reset(this.settings.access.onboarding_seen);
    // Fresh hire, fresh training: skill pips and the HUD pilot seat reset.
    this.captainSkills = freshCaptainSkills();
    this.prevPlayerPos = { x: p.pos.x, y: p.pos.y };
    this.hud.setCaptainSkills(this.captainSkills.pips);
    this.hud.setCaptainName(this.captain.name);
    void this.getCaptainPortrait().then((c) => this.hud.setCaptainPortrait(c));
    this.waveDamage = 0;
    this.lastSignificantTick = 0;
    // Death-window state: a restart during/after the cinematic restores full
    // speed and rearms the panel delay.
    this.deathSequenceTicks = 0;
    this.bossIntroTicks = 0;
    this.driver.dilation = 1;
    this.audio.startMusic(this.rngSeed);
  }

  /** Entropy for run seeds only — the sim stays deterministic per seed. */
  private rollSeed(): number {
    return (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
  }

  /** The seed the next run will use (drives the loadout ship preview);
   * `reroll: true` discards it and rolls a new hull. */
  getUpcomingShipSeed(reroll = false): number {
    if (reroll) this.pendingSeed = this.rollSeed();
    return this.pendingSeed;
  }

  /** The captain the next run will hire; `reroll: true` hires someone else. */
  getUpcomingCaptainSeed(reroll = false): number {
    if (reroll) this.pendingCaptainSeed = rollCaptainSeed();
    return this.pendingCaptainSeed;
  }

  /** Current captain's portrait, composited once per run (atlas when it
   * lands, placeholder bust or pixel character per portrait_style). */
  private getCaptainPortrait(): Promise<HTMLCanvasElement> {
    const style = this.settings.video.portrait_style;
    if (
      this.captainPortrait &&
      this.captainPortraitSeed === this.captainSeed &&
      this.captainPortraitStyle === style
    ) {
      return Promise.resolve(this.captainPortrait);
    }
    const seed = this.captainSeed;
    return compositeCaptain(this.captain, style).then((canvas) => {
      if (this.captainSeed === seed && this.settings.video.portrait_style === style) {
        this.captainPortrait = canvas;
        this.captainPortraitSeed = seed;
        this.captainPortraitStyle = style;
      }
      return canvas;
    });
  }

  /** The captain speaks: a mood won the machine, so pick a line in their
   * voice and show it. Death is terminal — DNA goes to the memorial first. */
  private onCaptainMood(mood: CaptainMood): void {
    // Silent during the loadout screensaver — the run hasn't started yet.
    if (this.loadoutScreen.isOpen()) return;
    if (mood === 'death') {
      const line = pickDeathLine(this.captain.temperament, this.captain.pronouns, Math.random);
      this.lastDeathLine = line;
      saveCaptainDNA(
        this.captain,
        { wave: getWaveInfo(this.world).displayWave, kills: this.scoreKills, seconds: Math.floor(this.world.tick / 60) },
        line
      );
      // The death bark shows the captain's face (async composite is fine —
      // the chip holds for seconds).
      void this.getCaptainPortrait().then((p) => this.captainSpeech.say(line, 7000, p));
      return;
    }
    const line = pickLine(this.captain.temperament, this.captain.pronouns, mood, Math.random);
    void this.getCaptainPortrait().then((p) => this.captainSpeech.show(line, mood, p));
  }

  /** The pause panel's ship visual: the hull you actually fly (run seed +
   * loadout), the same preview the loadout screen shows. */
  private buildPauseShipCanvas(): HTMLCanvasElement {
    const ship = document.createElement('canvas');
    ship.width = 128;
    ship.height = 128;
    ship.className = 'la-memorial-canvas';
    renderShipPreview(
      ship,
      this.world.runSeed,
      { swift: this.loadout.swift, bulwark: this.loadout.bulwark, striker: this.loadout.striker },
      this.loadout.weapon
    );
    return ship;
  }

  /** The pause panel's full info payload (mockup layout): timer, wave,
   * kills, scrap, seed, ship preview + name, captain portrait + name. */
  private buildPauseInfo(): PauseInfo {
    const score = this.getScore();
    return {
      runTimeSec: this.world.tick / 60,
      wave: getWaveInfo(this.world).displayWave,
      kills: score.kills,
      score: score.total,
      seed: this.world.runSeed,
      shipName: rollShipName(this.world.runSeed),
      shipCanvas: this.buildPauseShipCanvas(),
      captainName: this.captain.name.split(' ')[0],
      captainPortrait: this.getCaptainPortrait(),
    };
  }

  setPaused(pause: boolean): void {
    // The run is already finished — no pausing over the end screen.
    if (pause && this.world.gameOver) return;
    this.paused = pause;
    if (pause) {
      this.menus.showPause(this.buildPauseInfo());
    } else {
      // Drop edges queued while the sim was unsampled (touch II taps, held
      // keys) so they can't fire into the resumed run.
      this.inputManager.clearPressedEdges();
      this.menus.hide();
    }
  }

  applySettings(settings: Settings): void {
    this.settings = settings;
    // The auto-scaler resumes from the (possibly new) slider ceiling; when
    // auto is off the slider value applies directly.
    this.autoScale = settings.video.render_scale;
    // Bloom/particle budgets follow the mobile profile live; the enemy pool
    // cap is fixed at construction, so a pool-size change takes full effect
    // on the next page load.
    this.renderer.applySettings(this.renderSettings(settings));
    this.audio.setMaster(settings.audio.master);
    this.audio.setSfx(settings.audio.sfx);
    this.audio.setMusicVolume(settings.audio.music);
    this.hud.setShowFps(cfg.getBoolean('dev.overlay'));
    this.inputManager.showTouchControls(settings.access.show_touch_controls);
    this.haptics.setEnabled(settings.access.haptics_enabled);
    saveSettings(settings);
  }

  /**
   * Settings view handed to the renderer. With the mobile performance profile
   * enabled, bloom intensity is scaled by perf.mobile_bloom_scale and particle
   * emission is reduced; the player's own settings object is never mutated.
   * Desktop (mobile_performance: false) passes through unchanged.
   */
  private renderSettings(settings: Settings): Settings {
    if (!settings.video.mobile_performance) return settings;
    return {
      ...settings,
      video: {
        ...settings.video,
        bloom_intensity: settings.video.bloom_intensity * cfg.getNumber('perf.mobile_bloom_scale'),
        reduced_particles: true
      }
    };
  }

  /** Current run score. Deterministic: built from sim events + tick only.
   * Wallet semantics: total = earned − spent (shop purchases gate on the
   * total, so it never drops below 0; the clamp is belt-and-suspenders). */
  getScore(): RunScore {
    const timeBonus = Math.floor(this.world.tick / 60) * SCORE_PER_SECOND;
    const earned =
      this.scoreKills * SCORE_PER_KILL +
      this.scoreSubspace +
      this.scorePickups * SCORE_PER_PICKUP +
      timeBonus +
      this.scoreBravery +
      this.scoreWaves +
      this.scoreGestation;
    return {
      kills: this.scoreKills,
      pickups: this.scorePickups,
      motes: this.scoreMotes,
      timeBonus,
      bravery: this.scoreBravery,
      waves: this.scoreWaves,
      gestation: this.scoreGestation,
      subspace: this.scoreSubspace,
      spent: this.scoreSpent,
      total: Math.max(0, earned - this.scoreSpent)
    };
  }

  /**
   * Bravery call: skip the rest of the intermission and start the assault
   * now, banking the bonus that was on the button. No-op outside
   * intermissions (callWaveEarly re-checks, so a stale click can't double
   * fire). The bonus rides pendingBraveryBonus into the WAVE banner the
   * wave_start event fires this same step.
   */
  private callNextWaveEarly(tick: number): void {
    if (this.world.gameOver) return;
    // No wave calls from inside a subspace trip (the wave clock is suspended).
    if (this.world.bonus.phase !== 'idle') return;
    const info = getWaveInfo(this.world);
    if (info.callBonus <= 0) return;
    if (callWaveEarly(this.world, tick) <= 0) return;
    this.scoreBravery += info.callBonus;
    this.pendingBraveryBonus = info.callBonus;
    // Daring training: the captain learns to love the odds.
    const lvl = noteBravery(this.captainSkills);
    if (lvl !== null) this.applySkillLevel('daring', lvl, tick);
  }

  /** Shop boon rank: Lumensurge ranks plus this shop's own grants (the
   * BonusGame boonRanks pattern), so max_rank holds across systems. */
  private shopRankOf(id: string): number {
    return this.upgradeSystem.getRank(id) + (this.shopRanks.get(id) ?? 0);
  }

  /** Stock of the current opening as overlay views (recomputed per render —
   * the seeded roll is idempotent, so a post-purchase refresh agrees). */
  private buildShopEntries(): ShopEntryView[] {
    const wallet = this.getScore().total;
    const stock = describeStock(rollShopStock(this.world.runSeed, this.shopWave));
    return stock.map((item) => {
      const rarity = boonRarityFor(item.id);
      const price = priceFor(item, this.shopWave);
      return {
        id: item.id,
        icon: item.icon,
        name: item.name,
        effect: item.effect,
        cost: price,
        affordable: canAfford(wallet, price),
        sold: this.shopSold.has(item.id),
        soldOut: rarity
          ? rollShopBoon(this.world.runSeed, this.shopWave, rarity, this.world.player, (u) => this.shopRankOf(u)) === null
          : false,
      };
    });
  }

  /**
   * Open the supply drop: intermission only, run live, nothing else pausing
   * the sim (mirrors the upgrade overlay's guards — no double-pause when the
   * boons modal is up, no shop over the pause menu or the end screen).
   */
  private openShop(): void {
    if (this.world.gameOver || this.paused) return;
    if (this.shopOverlay.isOpen() || this.upgradeOverlay.isOpen()) return;
    const info = getWaveInfo(this.world);
    // No shop while a wormhole subspace trip is running (the wave clock is
    // suspended mid-whatever, so the intermission check alone isn't enough).
    if (info.phase !== 'intermission' || this.world.bonus.phase !== 'idle') return;
    this.shopWave = info.displayWave;
    // Stock and SOLD state are per WAVE, not per opening: re-opening the shop
    // in the same intermission must never re-sell what was already bought.
    if (this.shopWave !== this.shopSoldWave) {
      this.shopSold.clear();
      this.shopSoldWave = this.shopWave;
    }
    this.shopOverlay.show(this.buildShopEntries(), this.getScore().total);
  }

  private closeShop(): void {
    this.shopOverlay.hide();
    // Keys pressed while the shop held the sim paused (above all the B or
    // Escape that closed it) must not leak into the resumed sim.
    this.inputManager.clearPressedEdges();
  }

  /**
   * Buy one stock entry: gate on the wallet total, apply the effect
   * immediately, and mark the entry SOLD for this opening. A boon with no
   * eligible upgrade never reaches here (the button reads SOLD OUT); the
   * early return is the belt-and-suspenders refund — no score changes hands.
   */
  private buyShopItem(id: ShopItemId): void {
    if (!this.shopOverlay.isOpen() || this.shopSold.has(id)) return;
    const item = shopItem(id);
    const price = priceFor(item, this.shopWave);
    if (!canAfford(this.getScore().total, price)) return;
    const rarity = boonRarityFor(id);
    let banner: string;
    if (rarity) {
      const def = rollShopBoon(this.world.runSeed, this.shopWave, rarity, this.world.player, (u) => this.shopRankOf(u));
      if (!def) return;
      this.scoreSpent += price;
      this.shopRanks.set(def.id, (this.shopRanks.get(def.id) ?? 0) + 1);
      applyUpgradeToPlayer(this.world.player, def);
      banner = `BOON: ${def.display_name}`;
    } else {
      this.scoreSpent += price;
      applyShopItem(this.world.player, id);
      banner =
        id === 'repair' ? `REPAIRED +${REPAIR_AMOUNT} HP`
        : id === 'hull' ? `HULL +${HULL_AMOUNT}`
        : id === 'flash_cell' ? `FLASH CHARGE +${FLASH_CELL_CHARGES}`
        : `MAX HP +${VITALS_AMOUNT}`;
    }
    this.shopSold.add(id);
    this.hud.showBanner(banner, 'wave');
    this.haptics.play(HAPTIC_PATTERNS.pickup);
    this.shopOverlay.update(this.buildShopEntries(), this.getScore().total);
  }

  /**
   * A captain skill pip leveled: fold the per-pip mods into the captain's
   * live effect set (read by getTraitMods), announce it for render/audio,
   * and update the HUD pilot seat.
   */
  private applySkillLevel(skill: SkillId, pips: number, tick: number): void {
    const cfx = this.world.player.captainEffects;
    if (cfx) {
      const mods = SKILL_PIP_MODS[skill] as Record<string, number>;
      const target = cfx as unknown as Record<string, number>;
      for (const [key, value] of Object.entries(mods)) {
        target[key] = (target[key] ?? 0) + value;
      }
    }
    this.world.events.emit('captain_skill', { skill, pips }, tick);
    this.hud.setCaptainSkills(this.captainSkills.pips);
  }

  private trackScoreEvent(e: GameEvent): void {
    if (e.type === 'death' && e.payload.kind === 'enemy') {
      // Subspace kill (bonus fight): credits the multiplier value INSTEAD of
      // the base per-kill score. The normal pool is detached while the fight
      // runs (state.ts pool swap), so a fighting-phase enemy death is a
      // subspace kill by construction. Objective/trip counting lives in
      // BonusGame's own event subscription (sim/bonus-game.ts).
      if (this.world.bonus.phase === 'fighting') {
        this.scoreSubspace += SUBSPACE_KILL_SCORE;
      } else {
        this.scoreKills++;
      }
      // Denial bonus: killing an enemy while it gestates (telegraph running).
      if (e.payload.gestating === true) this.scoreGestation += GESTATION_BONUS;
      // Gunnery training: every kill sharpens the captain.
      const lvl = noteKill(this.captainSkills);
      if (lvl !== null) this.applySkillLevel('gunnery', lvl, e.tick);
    } else if (e.type === 'hit' && e.payload.weapon_id === 'weapon.manual-laser-cannon') {
      // Deadeye training: the captain drills the manual cannon by landing it.
      const lvl = noteTurretHit(this.captainSkills);
      if (lvl !== null) this.applySkillLevel('deadeye', lvl, e.tick);
    } else if (e.type === 'pickup') {
      // Salvage training: every pickup counts toward a bigger magnet.
      const slvl = notePickup(this.captainSkills);
      if (slvl !== null) this.applySkillLevel('salvage', slvl, e.tick);
      this.scorePickups++;
      if (e.payload.kind === 'xp_mote') {
        const value = Number(e.payload.value) || 0;
        this.scoreMotes += value;
        this.upgradeSystem.addMotes(value, e.tick);
      }
    }
  }

  private pickUpgrade(index: number): void {
    // The overlay shows either a Lumensurge offer or the bonus game's boon.
    if (this.bonusGame.phase === 'boon') {
      this.bonusGame.chooseBoon(index, this.world.tick);
    } else {
      this.upgradeSystem.choose(index, this.world.tick);
    }
    this.upgradeOverlay.hide();
  }

  private rerollUpgrades(): void {
    // Boons are a fixed rare/mythical draw; rerolls belong to Lumensurge.
    if (this.bonusGame.phase === 'boon') return;
    if (this.upgradeSystem.reroll(this.world.tick)) {
      this.upgradeOverlay.show(
        this.upgradeSystem.getChoices(),
        this.upgradeSystem.getRerollsLeft()
      );
    }
  }

  private makeStepper(): Stepper {
    return {
      step: (tick: number) => {
        // The game-over panel freezes the sim: after the death cinematic the
        // world behind the panel must be truthful, not a zombie run.
        if (!this.paused && !(this.world.gameOver && this.deathSequenceTicks <= 0)) {
          this.stepSimulation(tick);
        }
        this.world.events.flush(tick);

        // Level-up cue at FULL speed: when an offer is ready, fire a gold
        // banner + ring and open the boons overlay a short beat later. No
        // time dilation — slow-mo read as a sudden FPS drop.
        if (
          !this.paused &&
          !this.world.gameOver &&
          !this.loadoutScreen.isOpen() &&
          this.upgradeSystem.hasPendingOffer() &&
          !this.upgradeOverlay.isOpen()
        ) {
          if (this.offerDelayTicks <= 0) {
            this.offerDelayTicks = 18; // 0.3 s beat at full speed
            this.hud.showBanner('LUMENSURGE UP', 'clear');
            this.renderer.playLevelUpCue(tick);
          }
          if (this.offerDelayTicks > 0) {
            this.offerDelayTicks--;
            if (this.offerDelayTicks <= 0) {
              this.upgradeOverlay.show(
                this.upgradeSystem.getChoices(),
                this.upgradeSystem.getRerollsLeft()
              );
            }
          }
        } else if (this.offerDelayTicks > 0) {
          // The offer was consumed externally (e.g. reroll).
          this.offerDelayTicks = 0;
        }

        // Wormhole bonus reward: when the bonus game reaches its boon phase,
        // show the 3 rare/mythical boon choices (no rerolls; the overlay
        // pauses the sim while open). Yields to an open Lumensurge overlay.
        if (
          !this.paused &&
          !this.world.gameOver &&
          !this.loadoutScreen.isOpen() &&
          this.bonusGame.phase === 'boon' &&
          !this.upgradeOverlay.isOpen()
        ) {
          this.upgradeOverlay.show(this.bonusGame.getBoonChoices(), 0, {
            filterRarities: ['rare', 'mythical']
          });
        }
      },
      render: (tick: number, alpha: number) => {
        // Dynamic resolution: hold ~60 fps by scaling the render buffer with
        // every effect left on. EMA of wall frame time evaluated every 2.5 s
        // (hysteresis); the settings slider is the ceiling, 0.5 the floor.
        if (this.settings.video.render_scale_auto && !this.world.gameOver) {
          const now = performance.now();
          if (this.lastAutoFrameAt > 0) {
            const dtMs = now - this.lastAutoFrameAt;
            if (dtMs > 0 && dtMs < 250) {
              this.frameMsEma = this.frameMsEma === 0 ? dtMs : this.frameMsEma * 0.9 + dtMs * 0.1;
            }
          }
          this.lastAutoFrameAt = now;
          if (now >= this.nextScaleEvalAt && this.frameMsEma > 0) {
            this.nextScaleEvalAt = now + 2500;
            const cap = this.settings.video.render_scale;
            if (this.frameMsEma > 18.2 && this.autoScale > 0.5) {
              this.autoScale = Math.max(0.5, this.autoScale - 0.15);
              this.renderer.setRenderScale(this.autoScale);
            } else if (this.frameMsEma < 16.6 && this.autoScale < cap) {
              this.autoScale = Math.min(cap, this.autoScale + 0.1);
              this.renderer.setRenderScale(this.autoScale);
            }
          }
        }
        // Subspace/wormhole visuals read world.bonus + world.wormhole
        // directly inside renderer.render — nothing to push here.
        // Radar specials: the wormhole, the subspace exit portal, and every
        // black hole off-screen.
        {
          const specials: { x: number; y: number; kind: 'wormhole' | 'blackhole' | 'exit' }[] = [];
          if (this.world.wormhole.active) {
            specials.push({ x: this.world.wormhole.pos.x, y: this.world.wormhole.pos.y, kind: 'wormhole' });
          }
          if (this.world.bonus.exit_portal.active) {
            specials.push({
              x: this.world.bonus.exit_portal.pos.x,
              y: this.world.bonus.exit_portal.pos.y,
              kind: 'exit'
            });
          }
          for (const w of this.renderer.getGravityWells()) {
            specials.push({ x: w.x, y: w.y, kind: 'blackhole' });
          }
          this.hud.setSpecialTargets(specials);
        }
        this.renderer.setSpaceStormState(this.spaceStorm.getRenderState());
        this.renderer.render(this.world, alpha);
        // Target acquisition never reaches beyond the visible circle; the
        // radius follows the current viewport/orientation.
        this.world.viewRadius = this.renderer.getVisibleRadius();
        // Captain mood machine: continuous evaluation for wary/bored/idle.
        {
          let enemiesNear = 0;
          for (const en of this.world.enemies.alive()) {
            const d = wrappedDelta(en.pos, this.world.player.pos);
            if (d.x * d.x + d.y * d.y < 100) enemiesNear++;
          }
          this.captainMoods.evaluate(performance.now(), {
            hp01: this.world.player.max_hp > 0 ? this.world.player.hp / this.world.player.max_hp : 0,
            enemiesNear,
            quietMs: ((this.world.tick - this.lastSignificantTick) * 1000) / 60,
          });
        }
        this.hud.setBonusActive(this.world.bonus.phase !== 'idle' || this.world.wormhole.active);
        this.hud.setSpaceStormActive(this.eventsManager.isActive(this.spaceStorm.id));
        this.hud.update(this.world, 1 / 60, this.getScore());
        // First-run hints: hidden while loadout/pause/game-over owns the UI.
        const playerSpeed = Math.hypot(this.world.player.vel.x, this.world.player.vel.y);
        this.onboarding.update(this.world.runTime, playerSpeed, !this.paused && !this.world.gameOver && !this.loadoutScreen.isOpen());
      }
    };
  }

  private stepSimulation(tick: number): void {
    this.world.tick = tick;
    this.world.runTime = tick * STEP;

    const input = this.inputManager.sample(this.world.player.pos, tick);
    // Input-aware hint copy: on touch-capable hardware, taps raise synthetic
    // mouse events — forwarding a 'mouse' sample flips the dash hint to
    // keyboard copy ("SPACE to dash") on phones. Real keyboards still reach
    // the hint via the 'keyboard' sample; only the mouse channel is gated.
    if (!(this.touchDevice && input.device === 'mouse')) {
      this.onboarding.setDevice(input.device);
    }

    // Pause edge (Escape key, touch pause button): show the pause menu and
    // skip this tick. Resume goes through the menu or the global Escape
    // handler (this edge is unsampled while the sim is paused).
    if (input.pausePressed) {
      this.setPaused(true);
      return;
    }

    // Galaxy zones grant a dash-recharge boost while the player is on top of
    // them. Applied as a temporary haste bonus on top of any upgrade haste.
    const zones = this.renderer.getGalaxyZones();
    const playerPos = this.world.player.pos;
    let galaxyHaste = 0;
    for (const z of zones) {
      const d = wrappedDelta(playerPos, z);
      if (d.x * d.x + d.y * d.y <= z.radius * z.radius) {
        galaxyHaste = 1;
        break;
      }
    }
    const p = this.world.player as unknown as { haste?: number; hasteFromUpgrades?: number };
    p.haste = (p.hasteFromUpgrades ?? 0) + galaxyHaste;

    this.applyBoons(tick);

    updatePlayer(this.world, input, STEP, tick);
    // Piloting training: distance flown this tick feeds the captain's pips.
    {
      const d = wrappedDelta(this.world.player.pos, this.prevPlayerPos);
      const dist = Math.hypot(d.x, d.y);
      this.prevPlayerPos = { x: this.world.player.pos.x, y: this.world.player.pos.y };
      if (dist > 1e-6) {
        const lvl = noteDistance(this.captainSkills, dist);
        if (lvl !== null) this.applySkillLevel('piloting', lvl, tick);
      }
    }
    // Serenity stance: fill/drain from this tick's velocity, then let the
    // weapon systems below read the tier perks (damage/rate/magnet).
    updateSerenity(this.world, tick);
    updateWeapons(this.world, tick);
    // Manual turret: one low-power shot per fire-press rising edge, aimed at
    // the tapped/clicked point when a pointer provided one. Gated by its own
    // cooldown inside fireManualLaser. Runs after updatePlayer so the Focus
    // Aim direction reflects this tick's input.
    if (input.firePressed) {
      const fired = fireManualLaser(this.world, tick, input.fireDir);
      // Weak отдача: while the hold-ground turret is deployed, each manual
      // shot kicks the camera a touch AWAY from the fire direction.
      if (fired && this.world.turret.deployed && input.fireDir) {
        this.renderer.addRecoil(input.fireDir.x, input.fireDir.y);
      }
    }
    // Bravery call: Enter or the HUD CALL button skips the rest of the
    // intermission. Resolved inside the sim step so the wave clock stays
    // tick-aligned and deterministic.
    if (input.callPressed || this.pendingWaveCall) {
      this.pendingWaveCall = false;
      this.callNextWaveEarly(tick);
    }
    // Supply drop: B or the HUD SHOP button opens the intermission shop.
    // Resolved inside the sim step like the bravery call; openShop re-checks
    // the intermission/pause guards, so a stale press can't double fire.
    if (input.shopPressed || this.pendingShopOpen) {
      this.pendingShopOpen = false;
      this.openShop();
    }
    updateProjectiles(this.world, STEP, tick);
    // Wormhole subspace: the bonus state machine + formation director run
    // BEFORE enemy steering so this tick's formation drives are in place when
    // updateEnemies reads them.
    this.bonusGame.update(tick);
    updateEnemies(this.world, STEP, tick);
    this.applyGravity(STEP);
    updateDirector(this.world, STEP, tick);
    // Wormhole: contact starts the subspace trip; its state machine advanced
    // above (warp-in → formation fight → boon/escape → warp-out).
    if (!this.loadoutScreen.isOpen() && updateWormhole(this.world, tick)) {
      this.bonusGame.start(tick);
    }
    // Timed run events (space storm, meteor shower, ion storm, gravity
    // well): seeded scheduling, one at a time. Runs after updateEnemies/
    // updateProjectiles by design — ion-storm post-adjusts the motion those
    // systems integrated this tick, and gravity-well perturbs the velocities
    // they will integrate next tick.
    this.eventsManager.update(this.world, tick);
    this.updatePickups(STEP, tick);
    this.applyHealing(tick);

    if (this.world.player.hp <= 0 && this.world.player.alive) {
      this.world.player.alive = false;
      this.world.gameOver = true;
      this.world.outcome = 'defeat';
      this.world.events.emit('game_over', { outcome: 'defeat' }, tick);
      // Only the defeat screen may remain: kill any open pause/menu state.
      this.paused = false;
      this.menus.hide();
      this.audio.stopMusic();
      // Dramatic death sequence: fast full-speed blaze opening, then
      // slow-motion ember afterglow. 10 ticks @ 1.0 + 39 ticks @ 0.22 lands
      // the panel at ≈3.1 s real time, matched to PLAYER_DEATH_DURATION_S.
      this.renderer.startPlayerDeath(this.world.player.pos, tick);
      this.deathSequenceTicks = 49;
      this.driver.dilation = 1;
    }

    // Delay the game-over panel until the death sequence finishes.
    if (this.deathSequenceTicks > 0) {
      this.deathSequenceTicks--;
      // Full speed while the blaze ignites, then time crawls for the burn.
      this.driver.dilation = this.deathSequenceTicks > 39 ? 1 : 0.22;
      if (this.deathSequenceTicks <= 0) {
        this.driver.dilation = 1;
        // The panel quotes the captain's final line — retire the corner bark
        // so the same words don't sit doubled under it.
        this.captainSpeech.hide();
        // The fallen: captain's face, their final line, and the ship they flew.
        const shipCanvas = document.createElement('canvas');
        shipCanvas.width = 96;
        shipCanvas.height = 96;
        renderShipPreview(
          shipCanvas,
          this.rngSeed,
          { swift: this.loadout.swift, bulwark: this.loadout.bulwark, striker: this.loadout.striker },
          this.loadout.weapon
        );
        this.menus.showGameOver(this.world, this.getScore(), {
          name: this.captain.name,
          deathLine: this.lastDeathLine,
          portrait: this.getCaptainPortrait(),
          ship: shipCanvas,
        });
      }
    }

    // Boss reveal beat: count down the slow-mo window and restore full speed
    // (the death cinematic outranks it and owns dilation while active).
    if (this.bossIntroTicks > 0 && this.deathSequenceTicks <= 0) {
      this.bossIntroTicks--;
      if (this.bossIntroTicks <= 0) this.driver.dilation = 1;
    }
  }

  /** Black holes pull entities and swallow nearby flashes (visual gravity). */
  /** Black holes are whirlpools, not vacuum cleaners: a tangential swirl
   * rides on top of the radial pull so anything caught orbits as it drains
   * (the pull reads as water circling a drain). Spin direction is per-well,
   * derived deterministically from its position. */
  private applyGravity(dt: number): void {
    const wells = this.renderer.getGravityWells();
    if (wells.length === 0) return;
    const pull = (pos: Vec2, vel: Vec2): void => {
      for (const w of wells) {
        const d = wrappedDelta({ x: w.x, y: w.y }, pos);
        const distSq = d.x * d.x + d.y * d.y;
        if (distSq > w.radius * w.radius || distSq < 1e-4) continue;
        const dist = Math.sqrt(distSq);
        // Same profile as the wormhole's pull field (see wormholePullAccel
        // in sim/wormhole.ts: cap 80, min dist² 9, strength 2400 per well):
        // pull beats player thrust inside ~6 u, so a flash is the escape.
        const force = Math.min(80, w.strength / Math.max(distSq, 9));
        vel.x += (d.x / dist) * force * dt;
        vel.y += (d.y / dist) * force * dt;
        // Swirl: perpendicular to the radial pull. Strongest at mid-range —
        // far out it's a gentle current, close in the radial drain wins.
        const spin = (Math.floor(w.x) + Math.floor(w.y)) % 2 === 0 ? 1 : -1;
        const mid = 1 - Math.abs(dist - w.radius * 0.5) / (w.radius * 0.5);
        const swirl = force * 1.1 * Math.max(0.25, mid);
        vel.x += (d.y / dist) * swirl * spin * dt;
        vel.y += (-d.x / dist) * swirl * spin * dt;
      }
    };
    pull(this.world.player.pos, this.world.player.vel);
    for (const e of this.world.enemies.alive()) {
      pull(e.pos, e.vel);
    }
    for (const p of this.world.projectiles.alive()) {
      pull(p.pos, p.vel);
    }
    for (const pk of this.world.pickups.alive()) {
      pull(pk.pos, pk.vel);
    }
  }

  /** Temporary boon effects from background objects (lucky-star, aegis-moon, swift-comet). */
  private applyBoons(_tick: number): void {
    const playerPos = this.world.player.pos;
    let crit = 0;
    let shieldRegen = 1;
    let moveSpeed = 1;
    for (const z of this.renderer.getBoonZones()) {
      const d = wrappedDelta(playerPos, z);
      if (d.x * d.x + d.y * d.y > z.radius * z.radius) continue;
      switch (z.kind) {
        case 'lucky-star':
          crit = 0.10;
          break;
        case 'aegis-moon':
          shieldRegen = 2;
          break;
        case 'swift-comet':
          moveSpeed = 1.2;
          break;
      }
    }
    this.world.player.boon_crit_chance = crit;
    this.world.player.boon_shield_regen_mult = shieldRegen;
    this.world.player.boon_move_speed_mult = moveSpeed;
  }

  /**
   * HP regen + green-galaxy healing, applied once per sim tick. Base regen
   * (1 HP/s) is gated on 3 s without damage; green galaxy zones heal 5 HP/s
   * unconditionally while the player is inside one. Both stack.
   */
  private applyHealing(tick: number): void {
    const p = this.world.player;
    if (!p.alive || p.hp <= 0 || p.hp >= p.max_hp) return;
    let heal = 0;
    if (tick - p.last_damage_tick >= HP_REGEN_DELAY_TICKS) heal += HP_REGEN_PER_TICK;
    const pos = p.pos;
    for (const z of this.getHealingZones()) {
      const d = wrappedDelta(pos, z);
      if (d.x * d.x + d.y * d.y <= z.radius * z.radius) {
        heal += GALAXY_HEAL_PER_TICK;
        break;
      }
    }
    if (heal > 0) p.hp = Math.min(p.max_hp, p.hp + heal);
  }

  /**
   * Green/teal galaxy zones from the background layer. renderer.ts is
   * outside this change's file scope, so the getHealingZones() passthrough
   * is read duck-typed; until integration adds it there, all galaxy zones
   * (renderer.getGalaxyZones) count as healing zones.
   */
  private getHealingZones(): GalaxyZone[] {
    const r = this.renderer as unknown as { getHealingZones?: () => GalaxyZone[] };
    return r.getHealingZones ? r.getHealingZones() : this.renderer.getGalaxyZones();
  }

  private updatePickupClusters(dt: number, tick: number): void {
    this.pickupClusterCounts.fill(0);
    this.pickupClusterSumX.fill(0);
    this.pickupClusterSumY.fill(0);

    let activeCellCount = 0;
    let moteCount = 0;
    for (const pk of this.world.pickups.alive()) {
      if (pk.pickup_kind !== 'xp_mote') continue;
      const cell = pickupClusterCellCoord(pk.pos.x) + pickupClusterCellCoord(pk.pos.y) * PICKUP_CLUSTER_CELLS;
      if (this.pickupClusterCounts[cell] === 0) {
        this.pickupClusterActiveCells[activeCellCount] = cell;
        activeCellCount += 1;
      }
      this.pickupClusterCounts[cell] += 1;
      this.pickupClusterSumX[cell] += pk.pos.x;
      this.pickupClusterSumY[cell] += pk.pos.y;
      moteCount += 1;
    }
    if (moteCount < 2) return;

    const time = tick / 60;
    for (const pk of this.world.pickups.alive()) {
      if (pk.pickup_kind !== 'xp_mote') continue;
      const baseCx = pickupClusterCellCoord(pk.pos.x);
      const baseCy = pickupClusterCellCoord(pk.pos.y);
      let fx = Math.sin(time * 0.9 + pk.pool_index * 0.73) * PICKUP_CLUSTER_DRIFT;
      let fy = Math.cos(time * 0.8 + pk.pool_index * 0.61) * PICKUP_CLUSTER_DRIFT;
      let longFx = 0;
      let longFy = 0;

      for (let oy = -1; oy <= 1; oy += 1) {
        const cy = (baseCy + oy + PICKUP_CLUSTER_CELLS) % PICKUP_CLUSTER_CELLS;
        for (let ox = -1; ox <= 1; ox += 1) {
          const cx = (baseCx + ox + PICKUP_CLUSTER_CELLS) % PICKUP_CLUSTER_CELLS;
          const cell = cx + cy * PICKUP_CLUSTER_CELLS;
          const count = this.pickupClusterCounts[cell];
          if (count === 0) continue;
          const sameCell = ox === 0 && oy === 0;
          const mass = sameCell ? count - 1 : count;
          if (mass <= 0) continue;
          const centerX = this.pickupClusterSumX[cell] / count;
          const centerY = this.pickupClusterSumY[cell] / count;
          const dx = wrapAxisDelta(centerX, pk.pos.x);
          const dy = wrapAxisDelta(centerY, pk.pos.y);
          const distSq = dx * dx + dy * dy;
          if (distSq < 1e-4) continue;
          const dist = Math.sqrt(distSq);
          const desired = 1.25 + Math.min(2.5, Math.sqrt(mass) * 0.55) + (sameCell ? 0.45 : 0);
          const radius = PICKUP_CLUSTER_RADIUS + Math.min(4, mass * 0.35);
          if (dist > desired && dist < radius) {
            const falloff = 1 - dist / radius;
            const force = (dist - desired) * PICKUP_CLUSTER_PULL * falloff * (0.45 + mass * 0.12);
            fx += (dx / dist) * force;
            fy += (dy / dist) * force;
          } else if (sameCell && dist < desired * 0.72) {
            const force = (desired * 0.72 - dist) * PICKUP_CLUSTER_REPEL;
            fx -= (dx / dist) * force;
            fy -= (dy / dist) * force;
          }
        }
      }

      for (let i = 0; i < activeCellCount; i += 1) {
        const cell = this.pickupClusterActiveCells[i];
        const cx = cell % PICKUP_CLUSTER_CELLS;
        const cy = Math.floor(cell / PICKUP_CLUSTER_CELLS);
        const cellDx = Math.min(Math.abs(cx - baseCx), PICKUP_CLUSTER_CELLS - Math.abs(cx - baseCx));
        const cellDy = Math.min(Math.abs(cy - baseCy), PICKUP_CLUSTER_CELLS - Math.abs(cy - baseCy));
        if (cellDx <= 1 && cellDy <= 1) continue;
        const count = this.pickupClusterCounts[cell];
        if (count === 0) continue;
        const centerX = this.pickupClusterSumX[cell] / count;
        const centerY = this.pickupClusterSumY[cell] / count;
        const dx = wrapAxisDelta(centerX, pk.pos.x);
        const dy = wrapAxisDelta(centerY, pk.pos.y);
        const distSq = dx * dx + dy * dy;
        if (distSq < 36) continue;
        const dist = Math.sqrt(distSq);
        const force =
          (PICKUP_CLUSTER_LONG_PULL * Math.min(3.5, Math.sqrt(count))) /
          (1 + distSq * PICKUP_CLUSTER_LONG_FALLOFF);
        longFx += (dx / dist) * force;
        longFy += (dy / dist) * force;
      }

      const longMag = Math.hypot(longFx, longFy);
      if (longMag > PICKUP_CLUSTER_LONG_FORCE_CAP) {
        const scale = PICKUP_CLUSTER_LONG_FORCE_CAP / longMag;
        longFx *= scale;
        longFy *= scale;
      }
      fx += longFx;
      fy += longFy;

      pk.vel.x += fx * dt;
      pk.vel.y += fy * dt;
    }
  }

  private updatePickups(dt: number, tick: number): void {
    this.updatePickupClusters(dt, tick);
    const p = this.world.player.pos;
    // Mote Lure upgrade widens the collection radius via passive ranks;
    // Serenity tier 1 doubles it (collect without moving); a smooth captain
    // adds +15% (captain/effects.ts rides getTraitMods).
    const magnetMult =
      (1 + getUpgradeMods(this.world.player).magnetRadiusMult + getTraitMods(this.world.player).magnetRadiusMult) *
      getSerenityMods(this.world).magnetMult;
    const pickupRadius = (0.5 + this.world.player.hitbox_radius) * magnetMult;
    // The pull itself is RANGED: motes only home inside the magnet radius —
    // positioning matters again instead of every coin on the map slowly
    // converging on the ship for free.
    const magnetRadius = MAGNET_PULL_RADIUS * magnetMult;
    // Publish for the renderer's dashed magnet ring (render/magnet-ring.ts).
    this.world.magnetPullRadius = magnetRadius;
    const drag = Math.pow(PICKUP_DRAG_PER_STEP, dt * 60);
    for (const pk of this.world.pickups.alive()) {
      if (pk.magnet) {
        // Shortest signed delta from the pickup to the player across the seam.
        const d = wrappedDelta(p, pk.pos);
        const dist = Math.hypot(d.x, d.y);
        if (dist > 0.1 && dist < magnetRadius) {
          pk.vel.x += (d.x / dist) * 40 * dt;
          pk.vel.y += (d.y / dist) * 40 * dt;
        }
      }
      pk.vel.x *= drag;
      pk.vel.y *= drag;
      const vMag = Math.hypot(pk.vel.x, pk.vel.y);
      if (vMag > PICKUP_MAX_SPEED) {
        pk.vel.x = (pk.vel.x / vMag) * PICKUP_MAX_SPEED;
        pk.vel.y = (pk.vel.y / vMag) * PICKUP_MAX_SPEED;
      }
      pk.pos.x += pk.vel.x * dt;
      pk.pos.y += pk.vel.y * dt;
      wrapArena(pk.pos);
      const dc = wrappedDelta(pk.pos, p);
      if (Math.hypot(dc.x, dc.y) < pickupRadius) {
        // Health pickups (e.g. meteor-shower drops) heal on collection;
        // previously they emitted the event without applying any effect.
        if (pk.pickup_kind === 'health' && this.world.player.alive) {
          this.world.player.hp = Math.min(
            this.world.player.max_hp,
            this.world.player.hp + pk.value
          );
        }
        // Loot crates (spec-slots): resolve weapon → level → motes on the
        // 'loot' stream and announce the outcome for UI/audio. The 'pickup'
        // event below still fires exactly once for score tracking.
        if (pk.pickup_kind === 'powerup' && this.world.player.alive) {
          const crate = resolveLootCrate(this.world);
          if (crate.result === 'motes') {
            this.upgradeSystem.addMotes(LOOT_CRATE_MOTES, tick);
          }
          this.world.events.emit(
            'loot_crate',
            {
              result: crate.result,
              def_id: crate.result === 'motes' ? undefined : crate.def_id,
              pos: { x: pk.pos.x, y: pk.pos.y }
            },
            tick
          );
        }
        this.world.events.emit('pickup', { kind: pk.pickup_kind, pos: pk.pos, value: pk.value }, tick);
        this.world.pickups.release(pk);
      }
    }
  }

  private bindGlobalInput(): void {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('blur', this.onBlur);
    // Escape while paused: back out of settings to the pause panel, or
    // resume. (Pausing itself is the input.sample pausePressed edge in
    // stepSimulation — unsampled while paused, so resume needs this path.)
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.paused) return;
      e.preventDefault();
      if (this.menus.isInSettings()) {
        this.menus.showPause(this.buildPauseInfo());
      } else {
        this.setPaused(false);
      }
    });
  }

  private onResize = (): void => {
    this.renderer.resize();
  };

  private onBlur = (): void => {
    // Never pause over the loadout screen or an open upgrade/shop overlay; the
    // gameOver guard lives in setPaused itself.
    if (
      this.settings.access.pause_on_focus_loss &&
      !this.upgradeOverlay.isOpen() &&
      !this.shopOverlay.isOpen() &&
      !this.loadoutScreen.isOpen()
    ) {
      this.setPaused(true);
    }
  };
}
